import {
  type Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import {type SharedPanelZoomState} from '../SharedPanelZoomContext';
import {
  type HistoryPoint,
  type PartialZoom,
  type UserZoomIntentPayload,
} from '../types';
import {useDeepEqualValue} from './../../../util/hooks';
import {
  isAxisEntirelyOutsideConfigRange,
  isGestureExcludedByConfigZoom,
} from './utils';
import {buildUserZoomPatch, isZoomActive, ZOOM_KEYS} from './zoomIntent';

export const DEFAULT_ZOOM = {
  xAxisMax: null,
  xAxisMin: null,
  yAxisMax: null,
  yAxisMin: null,
};

type PanelZoomReducerState = {
  xAxisBoundary: {
    min: number | null;
    max: number | null;
  };
  userZoom: {
    xAxisMax: number | null;
    xAxisMin: number | null;
    yAxisMax: number | null;
    yAxisMin: number | null;
  };
  xAxisKey: string;
};

type PanelZoomReducerActions =
  | {
      type: 'panelZoom/changeXAxisKey';
      payload: string;
    }
  | {
      type: 'panelZoom/resetUserZoom';
      payload: PartialZoom;
    }
  | {
      type: 'panelZoom/sharedPanelZoomChange';
      payload: SharedPanelZoomState;
    }
  | {
      type: 'panelZoom/userZoomChange';
      payload: UserZoomIntentPayload;
    }
  | {
      type: 'panelZoom/toggleLogging';
      payload: boolean;
    };

export function panelZoomReducer(
  state: PanelZoomReducerState,
  action: PanelZoomReducerActions
) {
  switch (action.type) {
    case 'panelZoom/toggleLogging': {
      return {
        ...state,
        isLoggingOn: action.payload,
      };
    }

    /**
     * When the x-axis value changes we reset any active user zooms
     */
    case 'panelZoom/changeXAxisKey': {
      // effects can fire sometimes on mount, so don't reset in those cases
      if (action.payload === state.xAxisKey) {
        return state;
      }

      return {
        ...state,
        xAxisKey: action.payload,
        userZoom: DEFAULT_ZOOM,
      };
    }

    /**
     * When a config value changes we reset any active user zooms
     */
    case 'panelZoom/resetUserZoom': {
      return {
        ...state,
        userZoom: DEFAULT_ZOOM,
      };
    }

    /**
     * Note: when user zooms happen elsewhere, we still update the user zoom for this panel.
     * The logic for making sure the user zoom doesn't escape the boundaries is handled
     * outside the reducer in the consuming hook.
     */
    case 'panelZoom/sharedPanelZoomChange': {
      if (state.xAxisKey !== action.payload.xAxis) {
        return state;
      }

      // no-op shared panel zooms that match the current user zoom
      if (
        state.userZoom.xAxisMin === action.payload.xMin &&
        state.userZoom.xAxisMax === action.payload.xMax
      ) {
        return state;
      }

      return {
        ...state,
        userZoom: {
          ...state.userZoom,
          xAxisMin: action.payload.xMin ?? null,
          xAxisMax: action.payload.xMax ?? null,
        },
      };
    }

    case 'panelZoom/userZoomChange': {
      const userZoomPatch = buildUserZoomPatch(action.payload);
      const nextUserZoom = {...state.userZoom};
      ZOOM_KEYS.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(userZoomPatch, key)) {
          nextUserZoom[key] = userZoomPatch[key] ?? null;
        }
      });

      return {
        ...state,
        userZoom: nextUserZoom,
      };
    }

    default: {
      return state;
    }
  }
}

export const usePanelZoomReducer = ({
  coordinateZooming,
  configZoom,
  history: _history, // @deprecated - backend handles _absolute_runtime conversion
  sharedPanelZoomConfig,
  xAxisKey,
  userZoomState,
}: {
  coordinateZooming: boolean;
  configZoom: PartialZoom;
  history: HistoryPoint[]; // No longer used - backend handles _absolute_runtime conversion
  sharedPanelZoomConfig: SharedPanelZoomState;
  xAxisKey: string;
  userZoomState?: PartialZoom;
}) => {
  const lastConfig = useRef<PartialZoom>({
    xAxisMin: undefined,
    xAxisMax: undefined,
    yAxisMin: undefined,
    yAxisMax: undefined,
  });
  const [state, dispatch] = useReducer(panelZoomReducer, {
    xAxisKey,
    xAxisBoundary: {
      min: null,
      max: null,
    },
    // there might be an active user zoom in the URL params which will come through on mount
    userZoom: {
      ...DEFAULT_ZOOM,
      ...userZoomState,
    },
  });

  const configZoomMemo = useDeepEqualValue(configZoom ?? lastConfig.current);
  const userZoom = useDeepEqualValue(state.userZoom);

  useEffect(() => {
    if (!coordinateZooming) {
      return;
    }
    // §6.5 Config Zoom cap on propagated bounds: if the shared x range lies
    // entirely outside this panel's config x range, exclude the gesture
    // (no state mutation). Closure captures the latest configZoom; we omit it
    // from the dep list so the effect doesn't re-fire on config-only changes —
    // the separate config-changed effect below handles that case.
    const sharedZoomIsOutsideConfig = isAxisEntirelyOutsideConfigRange(
      sharedPanelZoomConfig.xMin,
      sharedPanelZoomConfig.xMax,
      configZoom.xAxisMin,
      configZoom.xAxisMax
    );
    if (sharedZoomIsOutsideConfig) {
      return;
    }
    dispatch({
      type: 'panelZoom/sharedPanelZoomChange',
      payload: sharedPanelZoomConfig,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinateZooming, sharedPanelZoomConfig]);

  // this effect can run more often than you expect, and it's tricky to debug
  // using a ref to check against means we don't false fire it
  useEffect(() => {
    if (
      ['xAxisMin', 'xAxisMax', 'yAxisMin', 'yAxisMax'].every(
        key =>
          configZoomMemo[key as keyof typeof configZoomMemo] ===
          lastConfig.current[key as keyof typeof lastConfig.current]
      )
    ) {
      return;
    }
    lastConfig.current = configZoomMemo;
    dispatch({
      type: 'panelZoom/resetUserZoom',
      payload: configZoomMemo,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [configZoomMemo]);

  const handleUserZoom = useCallback(
    (newZoom: UserZoomIntentPayload) => {
      const gestureExcluded = isGestureExcludedByConfigZoom(
        newZoom,
        configZoom
      );
      if (gestureExcluded) {
        return;
      }
      dispatch({
        type: 'panelZoom/userZoomChange',
        payload: newZoom,
      });
    },
    [configZoom]
  );

  useEffect(() => {
    dispatch({
      type: 'panelZoom/changeXAxisKey',
      payload: xAxisKey,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [xAxisKey]);

  const queryZoom = useMemo(() => {
    const result = getZoomCoordinates(state.userZoom, {
      xAxisMin: configZoom.xAxisMin ?? null,
      xAxisMax: configZoom.xAxisMax ?? null,
      yAxisMin: configZoom.yAxisMin ?? null,
      yAxisMax: configZoom.yAxisMax ?? null,
    });
    return result;
  }, [state.userZoom, configZoom]);

  return useMemo(() => {
    const isZooming = isZoomActive(userZoom);
    const value = {
      handleUserZoom,
      isZooming,
      queryZoom,
    };

    return value;
  }, [handleUserZoom, queryZoom, userZoom]);
};

export function getZoomCoordinates(
  userZoom: {
    xAxisMin: number | null | undefined;
    xAxisMax: number | null | undefined;
    yAxisMin: number | null | undefined;
    yAxisMax: number | null | undefined;
  },
  configZoom: {
    xAxisMin: number | null | undefined;
    xAxisMax: number | null | undefined;
    yAxisMin: number | null | undefined;
    yAxisMax: number | null | undefined;
  }
) {
  return {
    xAxisMin: getQueryValMin(
      userZoom.xAxisMin,
      configZoom.xAxisMin,
      configZoom.xAxisMax
    ),
    xAxisMax: getQueryValMax(
      userZoom.xAxisMax,
      configZoom.xAxisMin,
      configZoom.xAxisMax
    ),
    yAxisMin: getQueryValMin(
      userZoom.yAxisMin,
      configZoom.yAxisMin,
      configZoom.yAxisMax
    ),
    yAxisMax: getQueryValMax(
      userZoom.yAxisMax,
      configZoom.yAxisMin,
      configZoom.yAxisMax
    ),
  };
}

function getQueryValMin(
  userZoomValue: number | null | undefined,
  configMinValue: number | null | undefined,
  configMaxValue: number | null | undefined
): number | null {
  // if the user zoom value is null | undefined, return configMinValue or null
  if (userZoomValue == null) {
    return configMinValue ?? null;
  }

  // if the config values are null, return the user zoom value
  if (configMinValue == null && configMaxValue == null) {
    return userZoomValue;
  }

  // if userZoomValue exceeds configMaxValue, return default min
  if (configMaxValue != null && userZoomValue > configMaxValue) {
    return configMinValue ?? null;
  }

  // if userZoomValue is below configMinValue, return configMinValue, otherwise null
  if (configMinValue != null && userZoomValue < configMinValue) {
    return configMinValue;
  }

  // if userZoomValue is between configMinValue and configMaxValue, return userZoomValue
  if (
    configMinValue != null &&
    configMaxValue != null &&
    userZoomValue >= configMinValue &&
    userZoomValue <= configMaxValue
  ) {
    return userZoomValue;
  }

  // if configMinValue is null but configMaxValue exists, and userZoomValue is less than configMaxValue
  if (
    configMinValue == null &&
    configMaxValue != null &&
    userZoomValue < configMaxValue
  ) {
    return userZoomValue;
  }

  // if configMaxValue is null but configMinValue exists, and userZoomValue is greater than configMinValue
  if (
    configMaxValue == null &&
    configMinValue != null &&
    userZoomValue > configMinValue
  ) {
    return userZoomValue;
  }

  return null;
}

function getQueryValMax(
  userZoomValue: number | null | undefined,
  configMinValue: number | null | undefined,
  configMaxValue: number | null | undefined
) {
  if (userZoomValue == null) {
    return configMaxValue ?? null;
  }

  // if the config values are null, return the user zoom value
  if (configMinValue == null && configMaxValue == null) {
    return userZoomValue;
  }

  // if userZoomValue exceeds configMaxValue, return configMaxValue
  if (configMaxValue != null && userZoomValue > configMaxValue) {
    return configMaxValue;
  }

  // if userZoomValue is below configMinValue, return configMinValue, otherwise null
  if (configMinValue != null && userZoomValue < configMinValue) {
    return configMaxValue ?? null;
  }

  // if userZoomValue is between configMinValue and configMaxValue, return userZoomValue
  if (
    configMinValue != null &&
    configMaxValue != null &&
    userZoomValue >= configMinValue &&
    userZoomValue <= configMaxValue
  ) {
    return userZoomValue;
  }

  // if configMinValue is null but configMaxValue exists, and userZoomValue is less than configMaxValue
  if (
    configMinValue == null &&
    configMaxValue != null &&
    userZoomValue < configMaxValue
  ) {
    return userZoomValue;
  }

  // if configMaxValue is null but configMinValue exists, and userZoomValue is greater than configMinValue
  if (
    configMaxValue == null &&
    configMinValue != null &&
    userZoomValue > configMinValue
  ) {
    return userZoomValue;
  }

  return null;
}
